import {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {
    selectIsAuthenticated, selectAuthUserRole, selectAccessToken, selectFeatures
} from '@components/Login/userSlice';
import {selectIsDirectAuth} from '@components/appSlice';
import {
    selectIs2FAEnabled,
    selectHasDismissed2FA,
    selectIsTwoFactorStatusFetching,
    selectWasTwoFactorStatusFetched,
    fetchTwoFactorStatus
} from '@components/AccountSettings/TwoFactorAuth/twoFactorSlice';
import {AUTH_USER_ROLE} from '@constants/user';
import {IS_2FA_ENFORCEMENT_ENABLED} from '@constants/twoFactor';
import {parseJWT, getIsDelegationToken} from '@utils/jwtUtils';

const DISMISSED_2FA_STORAGE_KEY = 'wasDismissed2FA';

interface TwoFactorEnforcementStatus {
    isEnforced: boolean;
    isBlocking: boolean;
    isLoading: boolean;
    wasDismissedInCurrentSession: boolean;
}

function getSessionId(accessToken: string | null): string | null {
    if (!accessToken) return null;
    return parseJWT(accessToken)?.sid ?? null;
}

function getIsDismissedInCurrentSession(sessionId: string | null): boolean {
    if (!sessionId) return false;
    const storedSessionId = sessionStorage.getItem(DISMISSED_2FA_STORAGE_KEY);
    if (storedSessionId && storedSessionId !== sessionId) {
        sessionStorage.removeItem(DISMISSED_2FA_STORAGE_KEY);
        return false;
    }
    return storedSessionId === sessionId;
}

export function useGet2FAEnforcementStatus(): TwoFactorEnforcementStatus {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const authUserRole = useSelector(selectAuthUserRole);
    const accessToken = useSelector(selectAccessToken);
    const isDirectAuth = useSelector(selectIsDirectAuth);
    const is2FAEnabled = useSelector(selectIs2FAEnabled);
    const hasDismissed2FA = useSelector(selectHasDismissed2FA);
    const isFetching = useSelector(selectIsTwoFactorStatusFetching);
    const wasFetched = useSelector(selectWasTwoFactorStatusFetched);
    const features = useSelector(selectFeatures);

    const isDelegation = getIsDelegationToken(accessToken);
    const isSsoLoginEnabled = !!features?.ssoLogin;
    const isTargetUser = isAuthenticated && authUserRole === AUTH_USER_ROLE && !isDelegation && !isSsoLoginEnabled;
    const sessionId = getSessionId(accessToken);

    useEffect(() => {
        if (!IS_2FA_ENFORCEMENT_ENABLED) return;
        if (isDirectAuth && sessionId) {
            sessionStorage.setItem(DISMISSED_2FA_STORAGE_KEY, sessionId);
        }
    }, [isDirectAuth, sessionId]);

    useEffect(() => {
        if (!IS_2FA_ENFORCEMENT_ENABLED) return;
        if (isTargetUser && !wasFetched && !isFetching) {
            dispatch(fetchTwoFactorStatus() as any);
        }
    }, [isTargetUser, wasFetched, isFetching, dispatch]);

    if (!IS_2FA_ENFORCEMENT_ENABLED) {
        return {isEnforced: false, isBlocking: false, isLoading: false, wasDismissedInCurrentSession: false};
    }

    if (!isTargetUser || !wasFetched || isFetching) {
        return {isEnforced: false, isBlocking: false, isLoading: isTargetUser && (!wasFetched || isFetching), wasDismissedInCurrentSession: false};
    }

    const isEnforced = !is2FAEnabled;
    const hasDirectAuthSession = isDirectAuth && !!sessionId;
    const wasDismissedInCurrentSession = hasDirectAuthSession || getIsDismissedInCurrentSession(sessionId);

    return {
        isEnforced,
        isBlocking: isEnforced && hasDismissed2FA && !wasDismissedInCurrentSession,
        isLoading: false,
        wasDismissedInCurrentSession
    };
}

export {DISMISSED_2FA_STORAGE_KEY, getSessionId, getIsDismissedInCurrentSession};
